import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedInsurancePolicyDetail, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-insurance-policy-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, CustomerSelectorComponent, CurrencySelectorComponent, AinAmountPipe, AinDatePipe
  ],
  template: `
    <ain-page [title]="isCreate() ? t('sales.regulated.insurance.issuePolicy') : t('sales.regulated.insurance.policyDetailTitle')" [description]="t('sales.regulated.insurance.policiesDescription')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card"><a class="back-link" [routerLink]="['/sales/insurance-policies']">{{ t('catalog.action.backToList') }}</a></header>
        @if (isCreate()) {
          <section class="card grid">
            <label><span>{{ t('sales.regulated.insurance.fields.policyNumber') }}</span><input pInputText [(ngModel)]="form.policyNumber" /></label>
            <label><span>{{ t('sales.regulated.insurance.fields.premium') }}</span><p-inputNumber [(ngModel)]="form.premiumAmount" [min]="0" /></label>
            <label><span>{{ t('sales.installments.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            <label><span>{{ t('sales.regulated.insurance.fields.effectiveFrom') }}</span><input pInputText type="datetime-local" [(ngModel)]="form.effectiveFromLocal" /></label>
            <label><span>{{ t('sales.regulated.insurance.fields.effectiveTo') }}</span><input pInputText type="datetime-local" [(ngModel)]="form.effectiveToLocal" /></label>
            <label class="full"><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </section>
        } @else if (policy()) {
          <section class="summary card">
            <article><span>{{ t('sales.regulated.insurance.fields.policyNumber') }}</span><strong>{{ policy()!.policyNumber }}</strong></article>
            <article><span>{{ t('sales.regulated.insurance.fields.premium') }}</span><strong>{{ policy()!.premiumAmount | ainAmount }} {{ policy()!.currencyCode }}</strong></article>
            <article><span>{{ t('sales.regulated.insurance.fields.effectiveFrom') }}</span><strong>{{ policy()!.effectiveFromUtc | ainDate:'datetime' }}</strong></article>
            <article><span>{{ t('sales.regulated.insurance.fields.effectiveTo') }}</span><strong>{{ policy()!.effectiveToUtc | ainDate:'datetime' }}</strong></article>
          </section>
        }
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesInsurancePolicyDetailComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreate = signal(false);
  readonly policy = signal<RegulatedInsurancePolicyDetail | null>(null);
  form = {
    policyNumber: '',
    premiumAmount: 0,
    currencyCode: 'IRR',
    customerAccountId: null as string | null,
    proposalId: null as string | null,
    effectiveFromLocal: '',
    effectiveToLocal: ''
  };
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('policyId') ?? '';
    const premium = this.route.snapshot.queryParamMap.get('premium');
    this.isCreate.set(!id || id === 'new');
    if (premium) this.form.premiumAmount = Number(premium);
    if (this.isCreate()) { this.loading.set(false); return; }
    this.api.getInsurancePolicy(id).subscribe({
      next: p => { this.policy.set(p); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }
  save(): void {
    const payload = {
      proposalId: this.form.proposalId,
      customerAccountId: this.form.customerAccountId,
      policyNumber: this.form.policyNumber,
      premiumAmount: this.form.premiumAmount,
      currencyCode: this.form.currencyCode,
      effectiveFromUtc: new Date(this.form.effectiveFromLocal).toISOString(),
      effectiveToUtc: new Date(this.form.effectiveToLocal).toISOString()
    };
    this.api.issueInsurancePolicy(payload).subscribe({
      next: p => void this.router.navigate(['/sales/insurance-policies', p.id])
    });
  }
  t(key: string): string { return this.localization.translate(key); }
}
